// @flow

import {toast} from "react-toastify"
import i18n from "i18next"

import {del, put, post} from "../client"
import type {
  Invoice,
  ThunkAction,
  Action,
  Dispatch,
  ItemToInvoice,
} from "../types"

export const setInvoice = (invoice: Invoice): Action => ({
  type: "SET_INVOICE",
  invoice,
})

export const setItemsToInvoice = (
  itemsToInvoice: $ReadOnlyArray<ItemToInvoice>
): Action => ({
  type: "SET_INVOICE_ITEMS_TO_INVOICE",
  itemsToInvoice,
})

const setFinaliseInvoiceError = (finaliseError: boolean): Action => ({
  type: "SET_INVOICE_FINALISE_ERROR",
  finaliseError,
})

export const finaliseInvoice = (invoiceId: number): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(invoiceIsProcessing())
    dispatch(setFinaliseInvoiceError(false))
    return post(`/invoices/${invoiceId}/finalise`).then((response) => {
      if (response.data) {
        dispatch(setInvoice(response.data.invoice))
        dispatch(setItemsToInvoice(response.data.itemsToInvoice))
        toast.success(i18n.t("toast.invoicing.finaliseInvoice.success"))
      } else {
        dispatch(setFinaliseInvoiceError(true))
        toast.error(i18n.t("toast.invoicing.finaliseInvoice.error"))
      }
      dispatch(invoiceIsDoneProcessing())
    })
  }
}

export const regenerateInvoice = (invoiceId: number): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(invoiceIsProcessing())
    return post(`/invoices/${invoiceId}/regenerate`).then((response) => {
      if (response.data) {
        dispatch(setInvoice(response.data.invoice))
        dispatch(setItemsToInvoice(response.data.itemsToInvoice))
      } else {
        toast.error(i18n.t("toast.invoicing.regenerateInvoice.error"))
      }
      dispatch(invoiceIsDoneProcessing())
    })
  }
}

export const payInvoice = (invoiceId: number): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(invoiceIsProcessing())
    return post(`/invoices/${invoiceId}/pay`).then((response) => {
      if (response.data) {
        dispatch(setInvoice(response.data.invoice))
        dispatch(setItemsToInvoice(response.data.itemsToInvoice))
        toast.success(i18n.t("toast.invoicing.payInvoice.success"))
      } else {
        // data.errors == "invoice_not_valid"
        toast.error(i18n.t("toast.invoicing.payInvoice.error"))
      }
      dispatch(invoiceIsDoneProcessing())
    })
  }
}

export const updateInvoiceItem = (
  invoiceId: number,
  invoiceItemId: number,
  params: any
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(invoiceIsProcessing())
    return put(`/invoices/${invoiceId}/items/${invoiceItemId}`, params).then(
      (response) => {
        if (response.data) {
          dispatch(setInvoice(response.data.invoice))
          dispatch(setItemsToInvoice(response.data.itemsToInvoice))
        }
        dispatch(invoiceIsDoneProcessing())
      }
    )
  }
}

export const updateInvoiceMeta = (
  invoiceId: number,
  params: any
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(invoiceIsProcessing())
    return put(`/invoices/${invoiceId}`, transformParams(params)).then(
      (response) => {
        if (response.data) {
          dispatch(setInvoice(response.data.invoice))
          dispatch(setItemsToInvoice(response.data.itemsToInvoice))
        }
        dispatch(invoiceIsDoneProcessing())
      }
    )
  }
}

export const removeInvoiceItem = (
  invoiceId: number,
  invoiceItemId: number
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(invoiceIsProcessing())
    return del(`/invoices/${invoiceId}/items/${invoiceItemId}`).then(
      (response) => {
        if (response.data) {
          dispatch(setInvoice(response.data.invoice))
          dispatch(setItemsToInvoice(response.data.itemsToInvoice))
        }
        dispatch(invoiceIsDoneProcessing())
      }
    )
  }
}

export const addAllItemsToInvoice = (invoiceId: number): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(invoiceIsProcessing())
    return post(`/invoices/${invoiceId}/items/all`, {}).then((response) => {
      if (response.data) {
        dispatch(setInvoice(response.data.invoice))
        dispatch(setItemsToInvoice(response.data.itemsToInvoice))
      }
      dispatch(invoiceIsDoneProcessing())
    })
  }
}

export const addItemToInvoice = (
  invoiceId: number,
  itemId: number
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(invoiceIsProcessing())
    return post(`/invoices/${invoiceId}/items`, {itemId}).then((response) => {
      if (response.data) {
        dispatch(setInvoice(response.data.invoice))
        dispatch(setItemsToInvoice(response.data.itemsToInvoice))
      }
      dispatch(invoiceIsDoneProcessing())
    })
  }
}

// Not the best name, but here is the idea: the invoice object
// has a sender and receiver subobject, but in the DB,
// and on the server, it does not. So we have to map the props here before
// sending them to the server, so Phoenix and Ecto can play along
export const transformParams = (params: any) => {
  if (!params.sender && !params.receiver) return params
  let p = {
    ...params,
    senderPartner1Firstname: params.sender.partner1Firstname,
    senderPartner1Lastname: params.sender.partner1Lastname,
    senderPartner2Firstname: params.sender.partner2Firstname,
    senderPartner2Lastname: params.sender.partner2Lastname,
    senderStreet: params.sender.street,
    senderNbr: params.sender.nbr,
    senderBox: params.sender.box,
    senderCity: params.sender.city,
    senderPostalCode: params.sender.postalCode,
    senderCountry: params.sender.country,
    senderAccountNbr: params.sender.accountNbr,
    senderCompany: params.sender.company,
    senderFirstname: params.sender.firstname,
    senderLastname: params.sender.lastname,
    senderEmail: params.sender.email,
    senderVat: params.sender.vat,
    senderPhone: params.sender.phone,
    receiverPartner1Firstname: params.receiver.partner1Firstname,
    receiverPartner1Lastname: params.receiver.partner1Lastname,
    receiverPartner2Firstname: params.receiver.partner2Firstname,
    receiverPartner2Lastname: params.receiver.partner2Lastname,
    receiverStreet: params.receiver.street,
    receiverNbr: params.receiver.nbr,
    receiverBox: params.receiver.box,
    receiverCity: params.receiver.city,
    receiverPostalCode: params.receiver.postalCode,
    receiverCountry: params.receiver.country,
    receiverAccountNbr: params.receiver.accountNbr,
    receiverCompany: params.receiver.company,
    receiverFirstname: params.receiver.firstname,
    receiverLastname: params.receiver.lastname,
    receiverEmail: params.receiver.email,
    receiverEmailPartner: params.receiver.emailPartner,
    receiverVat: params.receiver.vat,
    receiverPhone: params.receiver.phone,
  }
  delete p.sender
  delete p.receiver
  return p
}

export const sendInvoice = (
  invoiceId: number,
  message: string
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(invoiceIsProcessing())
    return post(`/invoices/${invoiceId}/send`, {
      message,
    }).then((response) => {
      dispatch(invoiceIsDoneProcessing())
      if (!response.data.invoice) {
        return null
      }
      dispatch(setInvoice(response.data.invoice))
      if (response.data.invoice.isFinal) {
        toast.success(i18n.t("toast.invoicing.sendInvoice.success"))
        return
      }
      toast.success(i18n.t("toast.invoicing.sendInvoice.success_preview"))
    })
  }
}

const invoiceIsProcessing = (): Action => ({
  type: "SET_INVOICE_PROCESSING",
  isProcessing: true,
})

const invoiceIsDoneProcessing = (): Action => ({
  type: "SET_INVOICE_PROCESSING",
  isProcessing: false,
})
